import React, { useEffect, useRef } from 'react';
import EditorParent from '@/base-components/EditorParent';
import { customerName } from '@/generic/customerName';
import { Box, Center, Flex, GridItem, SimpleGrid, Spacer } from '@chakra-ui/react';
import { Context } from '@/App';
import { capitalizeFirstLetter } from '@/generic/string-utils';
import Text from '@/base-components/Text';
import { emptyCharacter, RoutePath, SPACING } from '@/generic/config';
import { useMutation, useQuery } from 'react-query';
import { QueryKeys } from '@/api/base/react-query';
import { getOverview, updatePriority, updateScheduledDate } from '@/api/order-controller';
import { OrderHit } from '@/model/order-hit.schema';
import { DndContext } from '@dnd-kit/core';
import Draggable from '@/generic/Draggable';
import Droppable from '@/generic/Droppable';
import useToast from '@/base-components/hooks/useToast';
import { Link } from 'react-router-dom';
import Icon, { Icons } from '@/base-components/Icons';
import Divider from '@/base-components/Divider';
import Dropdown from '@/base-components/Dropdown';
import Resource from '@/model/resource';
import { isEqual } from 'lodash';
import Input from '@/base-components/Input';
import usePalette from '@/base-components/hooks/usePalette';
import { isDesktop } from 'react-device-detect';

const REFETCH_INTERVAL = 10 * 60 * 1000;

const OverviewPage: React.FC = () => {
    const toast = useToast();
    const { translations, resources } = React.useContext(Context);
    const resourcesDropdownItems = resources?.data?.map(resource => ({ id: resource.id, name: resource.code }));
    const weeksToShowDropdownItems = Array.from({ length: 4 }, (_, i) => ({ id: (i + 1).toString(), name: `${i + 1} ${i + 1 === 1 ? translations.overview.week : translations.overview.weeks}` }));
    const [currentResource, setCurrentResource] = React.useState<Resource | null>(null);
    const lastFetchedParams = useRef<{ startDate: string, endDate: string, resourceId: number | null }>({ startDate: '', endDate: '', resourceId: null });

    const closeToastRef = React.useRef<(() => void) | null>(null);
    const [visibleWeeks, setVisibleWeeks] = React.useState(isDesktop ? 2 : 1);
    const [filterStartDate, setFilterStartDate] = React.useState<Date>(new Date());
    const [forceReRender, setForceReRender] = React.useState(0);
    const lastFetchedAt = useRef<Date | null>(null);
    const forceRerender = () => setForceReRender(prev => prev + 1);
    const workDays = getVisibleWorkdays(filterStartDate, visibleWeeks);
    const startDate = workDays[0];
    const endDate = workDays[workDays.length - 1];
    const overviewQuery = useQuery(QueryKeys.overview, () => {
        onLoading();
        return getOverview(startDate, endDate, currentResource?.id ?? null);
    }, {
        onSuccess: () => {
            onCloseToast();
            lastFetchedAt.current = new Date();
        }, onError: () => {
            onCloseToast();
        }
    });
    const onLoading = () => {
        onCloseToast();
        closeToastRef.current = toast(translations.overview.loading, "", "loading")
    };
    const onPerformingAction = () => {
        onCloseToast();
        closeToastRef.current = toast(translations.overview.performingAction, "", "loading")
    };
    const onCloseToast = () => closeToastRef.current?.();
    const performMoveMutation = useMutation({
        mutationFn: ({ orderId, newDate }: { orderId: number, newDate: string }) => {
            onPerformingAction();
            return updateScheduledDate(orderId, new Date(newDate));
        }, onSuccess: async () => {
            onCloseToast();
            await overviewQuery.refetch();
            toast(translations.overview.movedSuccessfully.title, translations.overview.movedSuccessfully.description, "success");
        }, onError: () => {
            onCloseToast();
        }
    });

    const performPriorityMutation = useMutation({
        mutationFn: ({ orderId, newPriority }: { orderId: number, newPriority: number }) => {
            onPerformingAction();
            return updatePriority(orderId, newPriority);
        }, onSuccess: async () => {
            onCloseToast();
            await overviewQuery.refetch();
            toast(translations.overview.priorityUpdatedSuccessfully.title, translations.overview.priorityUpdatedSuccessfully.description, "success");
        }, onError: () => {
            onCloseToast();
        }
    });

    useEffect(() => {
        const newFetchparams = {
            startDate: startDate.toISOString().split("T")[0],
            endDate: endDate.toISOString().split("T")[0],
            resourceId: currentResource?.id ?? null,
        };

        if (isEqual(lastFetchedParams.current, newFetchparams)) {
            return; // No need to refetch if parameters haven't changed
        }

        console.log("Fetching overview with params:", newFetchparams, lastFetchedParams.current);

        lastFetchedParams.current = newFetchparams;
        overviewQuery.refetch();
    }, [currentResource, overviewQuery, startDate, endDate]);

    return (
        <EditorParent title={translations.overview.title} description={translations.overview.description}>
            <SimpleGrid columns={isDesktop ? 10 : 6} gap={SPACING}>
                <GridItem colSpan={isDesktop ? 1 : 2}>
                    <Dropdown title={translations.overview.weeks} items={weeksToShowDropdownItems} value={weeksToShowDropdownItems.find(item => item.id === visibleWeeks.toString()) || null} onChange={(item) => setVisibleWeeks(item ? parseInt(String(item.id), 10) : 1)} />
                </GridItem>
                <GridItem colSpan={2}>
                    {resourcesDropdownItems && <Dropdown title={translations.model.order.resource} items={resourcesDropdownItems} value={currentResource ? { id: currentResource.id, name: currentResource.code } : null} onChange={(item) => setCurrentResource(item ? resources?.data?.find(resource => resource.id === item.id) || null : null)} />}
                </GridItem>
                <GridItem colSpan={2}>
                    <Input key={forceReRender} title={translations.model.order.filterDate} type="date" mb={SPACING / 4} defaultValue={filterStartDate.toISOString().split("T")[0]} onChange={(e) => setFilterStartDate(e.target.value ? new Date(e.target.value) : new Date())} />
                </GridItem>
                <GridItem alignContent="end" colSpan={isDesktop ? 5 : 6}>
                    <Flex mr={SPACING * 2} alignItems="center">
                        <Spacer />
                        <RefetchComponent key={`${overviewQuery.dataUpdatedAt}-${overviewQuery.errorUpdatedAt}`} refetch={overviewQuery.refetch} />
                        <Icon icon={Icons.reload} p={SPACING} cursor="pointer" onClick={() => overviewQuery.refetch()} />
                    </Flex>
                </GridItem>
            </SimpleGrid>
            <Flex flexGrow={1} mt={SPACING * 2} gap={SPACING / 2}>
                <DndContext onDragEnd={(event) => {
                    let orderId: number | undefined;
                    let currentDate: string | undefined;
                    let newDate: string | undefined;
                    let priority: number | undefined;
                    let prioritySlotOrderId: number | undefined;
                    try {
                        ({ orderId, shortDate: currentDate } = fromDraggableId(event.active.id.toString()));
                        ({ shortDate: newDate, priority, orderId: prioritySlotOrderId } = fromDroppableId(event.over?.id.toString()));
                    } catch (error) {
                        console.error("Error parsing IDs:", error);
                        return;
                    }

                    if (priority && prioritySlotOrderId && orderId !== undefined && prioritySlotOrderId !== orderId) {
                        performPriorityMutation.mutate({ orderId, newPriority: priority });
                        console.log(`Updated priority for order ${orderId} to ${priority}`);
                        return;
                    }

                    if (orderId > 0 && newDate.length > 0 && newDate !== currentDate) {
                        performMoveMutation.mutate({ orderId, newDate });
                        console.log(`Moved order ${orderId} from ${currentDate} to ${newDate}`);
                    }
                }}>
                    <Center>
                        <Icon icon={Icons.chevronLeft} cursor="pointer" onClick={() => {
                            setFilterStartDate(new Date(filterStartDate.getTime() - 7 * 24 * 60 * 60 * 1000))
                            forceRerender();
                        }} />
                    </Center>
                    {workDays.map((day, index) => {
                        const orders = overviewQuery.data?.filter(order => new Date(order.scheduledDate).toDateString() === day.toDateString()) || [];
                        const column = <OverviewColumn key={index} filterDate={filterStartDate} date={day} orders={orders} visibleWeeks={visibleWeeks} />;
                        return (
                            <React.Fragment key={index}>
                                {column}
                                {index % 5 === 4 && index !== workDays.length - 1 && <Divider vertical />}
                            </React.Fragment>
                        );
                    })}
                    <Center>
                        <Icon icon={Icons.chevronRight} cursor="pointer" onClick={() => {
                            setFilterStartDate(new Date(filterStartDate.getTime() + 7 * 24 * 60 * 60 * 1000));
                            forceRerender();
                        }} />
                    </Center>
                </DndContext>
            </Flex>
        </EditorParent>
    );
};

export default OverviewPage;

const RefetchComponent = ({ refetch }: { refetch: () => void }) => {
    const { translations } = React.useContext(Context);
    const [seconds, setSeconds] = React.useState(REFETCH_INTERVAL / 1000);

    React.useEffect(() => {
        const interval = setInterval(() => {
            setSeconds((prev) => {
                if (prev <= 1) {
                    refetch();
                    return REFETCH_INTERVAL / 1000;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [refetch]);

    if (seconds >= 60) {
        const minutes = Math.floor(seconds / 60);
        return (
            <Text>{translations.overview.refetching.minutes.replace("{minutes}", minutes.toString())}</Text>
        );
    }

    return (
        <Text>{translations.overview.refetching.seconds.replace("{seconds}", seconds.toString())}</Text>
    );
};

export const OverviewColumn = ({ filterDate, date, orders, visibleWeeks }: { filterDate: Date, date: Date, orders: OrderHit[], visibleWeeks: number }) => {
    const [isDragging, setIsDragging] = React.useState(false);
    const { language } = React.useContext(Context);
    const locale = language === 'SE' ? 'sv-SE' : 'en-US';
    const shortDate = date.toLocaleDateString("sv-SE");
    const isFilterDate = filterDate.toDateString() === date.toDateString();
    const isDifferentDate = (id: string | undefined) => {
        try {
            return fromDraggableId(id).shortDate !== shortDate;
        } catch {
            return false;
        }
    };

    return (
        <Box textAlign="center" w={`${100 / (visibleWeeks * 5)}%`} shadow="md" p={SPACING}>
            <Box h="100%">
                <Text variant="normal" fontWeight="medium" whiteSpace="nowrap">
                    {isFilterDate && "> "} {shortDate} {isFilterDate && " <"}
                </Text>
                <Text variant="normal">
                    {capitalizeFirstLetter(date.toLocaleDateString(locale, { weekday: 'long' }))}
                </Text>
                <Divider mb={SPACING} />
                <Droppable id={toDroppableId(shortDate)} isValid={(active) => isDifferentDate(active?.id?.toString())}>
                    <Box h="100%">
                        {orders
                            .sort((a, b) => a.priority - b.priority)
                            .sort((a, b) => a.express === b.express ? 0 : a.express ? -1 : 1)
                            .map((order, idx, arr) => {
                                const isExpress = order.express;
                                const prevPriority = idx > 0 ? arr[idx - 1].priority : null;
                                const nextPriority = idx < arr.length - 1 ? arr[idx + 1].priority : null;
                                const lastDropZonePriority = prevPriority && !isExpress ? order.priority - (order.priority - prevPriority) / 2 : order.priority - 10_000;
                                const nextDropZonePriority = nextPriority && !isExpress ? order.priority + (nextPriority - order.priority) / 2 : order.priority + 10_000;

                                return (
                                    <React.Fragment key={`${shortDate}-${order.id}`}>
                                        <Box>
                                            {isDragging && (
                                                <Droppable id={toDroppableId(shortDate, { priority: lastDropZonePriority, orderId: order.id })} isValid={(active) => true}>
                                                    <Text variant='extra-small' my={-SPACING * 1.25}>{emptyCharacter}</Text>
                                                </Droppable>
                                            )}
                                            {!isDragging && <Text variant='extra-small' my={-SPACING * 1.25}>{emptyCharacter}</Text>}
                                        </Box>
                                        <Draggable id={toDraggableId(shortDate, order.id)} onIsDragging={setIsDragging}>
                                            <OrderOverviewCard order={order} />
                                        </Draggable>
                                        <Box>
                                            {isDragging && (
                                                <Droppable id={toDroppableId(shortDate, { priority: nextDropZonePriority, orderId: order.id })} isValid={(active) => true}>
                                                    <Text variant='extra-small' my={-SPACING * 1.25}>{emptyCharacter}</Text>
                                                </Droppable>
                                            )}
                                            {!isDragging && <Text variant='extra-small' my={-SPACING * 1.25}>{emptyCharacter}</Text>}
                                        </Box>
                                    </React.Fragment>
                                );
                            })}
                    </Box>
                </Droppable>
            </Box>
        </Box>
    );
};

const toDroppableId = (shortDate: string, prioInfo?: { priority: number, orderId: number }) => prioInfo ? `droppable||${shortDate}||${prioInfo.priority}||${prioInfo.orderId}` : `droppable||${shortDate}`;
const fromDroppableId = (id: string | undefined) => {
    if (!id) throw new Error(`Invalid droppable id: ${id}`);
    if (id.startsWith('droppable||')) {
        const parts = id.split('||');
        return {
            shortDate: parts[1],
            priority: parts.length > 2 ? parseInt(parts[2], 10) : undefined,
            orderId: parts.length > 3 ? parseInt(parts[3], 10) : undefined,
        };
    }

    throw new Error(`Invalid droppable id: ${id}`);
}
const toDraggableId = (shortDate: string, orderId: number) => `draggable||${shortDate}||${orderId}`;
const fromDraggableId = (id: string | undefined) => {
    if (!id) throw new Error(`Invalid draggable id: ${id}`);
    const parts = id.split('||');
    if (parts.length === 3 && parts[0] === 'draggable') {
        return {
            shortDate: parts[1],
            orderId: parseInt(parts[2], 10),
        };
    }

    throw new Error(`Invalid draggable id: ${id}`);
};

const OrderOverviewCard = ({ order }: { order: OrderHit }) => {
    const { translations } = React.useContext(Context);
    const { colorScheme } = usePalette();
    const textStyles = {
        whiteSpace: "nowrap",
        overflowX: "hidden",
        textOverflow: "ellipsis",
    };

    return (
        <Box textAlign="start" shadow="sm" p={SPACING / 2}>
            <Flex>
                {order.express && (
                    <Text flex={1} variant="small" fontWeight="bold" bg={colorScheme.warning} textAlign="center" borderRadius={SPACING}>{translations.model.order.express.title}</Text>
                )}
                {order.blocked && (
                    <Text flex={1} variant="small" fontWeight="bold" bg={colorScheme.danger} textAlign="center" borderRadius={SPACING}>{translations.model.order.cards.holdInfo.hold}</Text>
                )}
            </Flex>
            <Flex>
                <Text variant="normal" fontWeight="semibold" {...textStyles}>{`${order.worksiteHit.city} , ${order.worksiteHit.postalCode}`}</Text>
                <Spacer />
                <Link to={`${RoutePath.Orders}/${order.id}`} target='_blank'>
                    <Icon size="xs" icon={Icons.openLink} />
                </Link>
            </Flex>
            <Text variant="small" {...textStyles}>{`${order.worksiteHit.address}`}</Text>
            <Text variant="small" fontWeight="semibold" {...textStyles}>{customerName(order.worksiteHit.customerHit, ", ")}</Text>
            {order.resource?.code && (
                <Flex>
                    <Spacer />
                    <Text variant="small" fontWeight="semibold" {...textStyles}>{`${order.resource.code}`}</Text>
                </Flex>
            )}
        </Box>
    )
};

/**
 * Returns an array of visible workdays for the overview page.
 * @param date - The date from which to calculate the visible workdays. usually the current date.
 * @returns 
 */
// TODO: Write unit tests for this function
const getVisibleWorkdays = (date: Date, visibleWeeks: number): Date[] => {
    const mondayThisWeek = new Date(date);
    mondayThisWeek.setDate(date.getDate() - date.getDay() + 1);
    const visibleWorkdays: Date[] = [];
    for (let i = 0; i < visibleWeeks; i++) {
        for (let j = 0; j < 5; j++) { // 5 workdays
            const workday = new Date(mondayThisWeek);
            workday.setDate(mondayThisWeek.getDate() + (i * 7) + j);
            visibleWorkdays.push(workday);
        }
    }
    return visibleWorkdays;
};